function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function deepCopy(value) {
  if (value === undefined) {
    return undefined;
  }
  return JSON.parse(JSON.stringify(value));
}

function getStartingMessageText(startingMessage) {
  if (hasText(startingMessage) === true) {
    return startingMessage.trim();
  }
  if (startingMessage !== null && startingMessage !== undefined && typeof startingMessage === 'object' && Array.isArray(startingMessage) === false) {
    const preferredKeys = ['default', 'en', 'he', 'ar'];
    for (let index = 0; index < preferredKeys.length; index = index + 1) {
      const value = startingMessage[preferredKeys[index]];
      if (hasText(value) === true) {
        return value.trim();
      }
    }
    const keys = Object.keys(startingMessage);
    for (let index = 0; index < keys.length; index = index + 1) {
      const value = startingMessage[keys[index]];
      if (hasText(value) === true) {
        return value.trim();
      }
    }
  }
  return '';
}

function makeRootNode(startingMessage, existingNode = null) {
  const existingData = existingNode !== null && existingNode !== undefined && existingNode.data !== null && existingNode.data !== undefined ? existingNode.data : {};
  const existingMessage = hasText(existingData.message) === true ? existingData.message : getStartingMessageText(startingMessage);
  return {
    id: existingNode !== null && existingNode !== undefined && hasText(existingNode.id) === true ? existingNode.id : 'root-0',
    type: 'Root',
    position: existingNode !== null && existingNode !== undefined && existingNode.position !== null && existingNode.position !== undefined ? existingNode.position : { x: 80, y: 220 },
    label: hasText(existingData.label) === true ? existingData.label : 'Start conversation',
    data: {
      label: hasText(existingData.label) === true ? existingData.label : 'Start conversation',
      stepDescription: hasText(existingData.stepDescription) === true ? existingData.stepDescription : '',
      nodeType: 'Root',
      isRoot: true,
      message: existingMessage,
      variablesToCollect: Array.isArray(existingData.variablesToCollect) === true ? existingData.variablesToCollect : [],
      toolsToUse: Array.isArray(existingData.toolsToUse) === true ? existingData.toolsToUse : [],
      buttons: Array.isArray(existingData.buttons) === true ? existingData.buttons : [],
      useExactMessage: existingData.useExactMessage === true
    },
    draggable: true,
    connectable: true,
    deletable: false
  };
}

function getDefaultActionData(actionType) {
  const base = {
    label: 'Action step',
    message: '',
    stepDescription: '',
    actionType,
    selectedToolId: null,
    toolSource: 'storedTool',
    inlineTool: {
      toolName: '',
      description: '',
      requiredParams: [],
      optionalParams: [],
      functionConfig: ''
    },
    toolInputMappings: {},
    variablesToCollect: [],
    buttons: [],
    useExactMessage: false,
    nodeType: 'Action',
    config: {}
  };
  if (actionType === 'httpRequest') {
    base.label = 'HTTP request';
    base.config = {
      method: 'POST',
      url: '',
      headersList: [],
      requestFields: [],
      body: '{}',
      outputVariable: '',
      responseMappings: []
    };
  }
  if (actionType === 'announce') {
    base.label = 'Announcement';
    base.useExactMessage = true;
    base.config = { allowUserInterrupt: true };
  }
  if (actionType === 'transferCall') {
    base.label = 'Transfer call';
    base.stepDescription = 'Transfer the live call to another person, team, phone number, or SIP destination.';
    base.config = {
      destination: '',
      phoneNumber: '',
      sipIp: '',
      sipPort: '',
      transferType: 'phoneNumber',
      lockTransferType: false,
      delayMode: 'afterAssistantReply',
      delayMs: 0
    };
  }
  if (actionType === 'endCall') {
    base.label = 'End call';
    base.stepDescription = 'End the live call when the conversation is complete.';
    base.config = {
      delayMode: 'afterAssistantReply',
      delayMs: 0
    };
  }
  return base;
}

function getJavaScriptNodeData() {
  const data = getDefaultActionData('activateTool');
  data.label = 'Run JavaScript';
  data.stepDescription = 'Run JavaScript using the current session variables.';
  data.toolSource = 'inlineJsTool';
  data.inlineTool = {
    toolName: 'JavaScript',
    description: 'Run code and return values to save as session variables.',
    requiredParams: [],
    optionalParams: [],
    functionConfig: 'return {};'
  };
  data.config = { isJavaScriptNode: true };
  return data;
}

function getBaseDefinitions() {
  return [
    {
      id: 'question',
      group: 'Conversation',
      type: 'Question',
      label: 'Ask a question',
      description: 'Say something, collect information, and continue.',
      icon: 'tabler:message-circle',
      data: {
        label: 'Question step',
        message: '',
        stepDescription: '',
        variablesToCollect: [],
        toolsToUse: [],
        buttons: [],
        useExactMessage: false,
        nodeType: 'Question'
      }
    },
    {
      id: 'knowledge',
      group: 'Conversation',
      type: 'KnowledgeBase',
      label: 'Search knowledge',
      description: 'Answer using the agent knowledge base.',
      icon: 'tabler:database-search',
      data: {
        label: 'Knowledge base step',
        stepDescription: '',
        variablesToCollect: [],
        buttons: [],
        nodeType: 'KnowledgeBase'
      }
    },
    {
      id: 'condition',
      group: 'Decisions',
      type: 'If',
      label: 'Make a decision',
      description: 'Choose a path using information already collected.',
      icon: 'tabler:git-branch',
      data: {
        label: 'Decision',
        stepDescription: '',
        nodeType: 'If',
        config: {
          sourceType: 'variable',
          sourceKey: '',
          operator: 'equals',
          left: '',
          right: ''
        }
      }
    },
    {
      id: 'tool',
      group: 'Actions',
      type: 'Action',
      label: 'Run a tool',
      description: 'Use one of the tools already connected to this agent.',
      icon: 'tabler:tool',
      data: getDefaultActionData('activateTool')
    },
    {
      id: 'http',
      group: 'Actions',
      type: 'Action',
      label: 'Call an API',
      description: 'Send an HTTP request and save values from the response.',
      icon: 'tabler:world-www',
      data: getDefaultActionData('httpRequest')
    },
    {
      id: 'javascript',
      group: 'Actions',
      type: 'Action',
      label: 'Run JavaScript',
      description: 'Use variables, call an approved API, and save computed values.',
      icon: 'tabler:code',
      data: getJavaScriptNodeData()
    },
    {
      id: 'announce',
      group: 'Voice actions',
      type: 'Action',
      label: 'Play an announcement',
      description: 'Say an exact message before continuing.',
      icon: 'tabler:speakerphone',
      supportedChannels: ['Phone', 'Web Voice Agent', 'Whatsapp Voice'],
      data: getDefaultActionData('announce')
    },
    {
      id: 'transfer',
      group: 'Phone actions',
      type: 'Action',
      label: 'Transfer the call',
      description: 'Send the live call to a phone or SIP destination.',
      icon: 'tabler:phone-outgoing',
      supportedChannels: ['Phone'],
      data: getDefaultActionData('transferCall')
    },
    {
      id: 'end',
      group: 'Voice actions',
      type: 'Action',
      label: 'End the call',
      description: 'Say a final message and finish the call.',
      icon: 'tabler:phone-off',
      supportedChannels: ['Phone', 'Web Voice Agent', 'Whatsapp Voice'],
      data: getDefaultActionData('endCall')
    }
  ];
}

function getToolPayload(tool) {
  if (tool !== null && tool !== undefined && typeof tool === 'object') {
    if (tool.toolData !== null && tool.toolData !== undefined && typeof tool.toolData === 'object') {
      return tool.toolData;
    }
    if (tool.tooldata !== null && tool.tooldata !== undefined && typeof tool.tooldata === 'object') {
      return tool.tooldata;
    }
  }
  return {};
}

function getToolIcon(toolName, payload) {
  const searchText = `${toolName} ${hasText(payload.description) === true ? payload.description : ''}`.toLowerCase();
  if (searchText.includes('gmail') === true) {
    return 'simple-icons:gmail';
  }
  if (searchText.includes('slack') === true) {
    return 'simple-icons:slack';
  }
  if (searchText.includes('github') === true) {
    return 'simple-icons:github';
  }
  if (searchText.includes('stripe') === true) {
    return 'simple-icons:stripe';
  }
  if (searchText.includes('notion') === true) {
    return 'simple-icons:notion';
  }
  if (searchText.includes('zendesk') === true) {
    return 'simple-icons:zendesk';
  }
  if (searchText.includes('salesforce') === true) {
    return 'simple-icons:salesforce';
  }
  if (searchText.includes('twilio') === true) {
    return 'simple-icons:twilio';
  }
  if (/(calendar|schedule|appointment|booking)/.test(searchText) === true) {
    return 'tabler:calendar-check';
  }
  if (/(email|mail|inbox)/.test(searchText) === true) {
    return 'tabler:mail';
  }
  if (/(customer|contact|crm|lead)/.test(searchText) === true) {
    return 'tabler:users';
  }
  if (/(payment|invoice|price|billing|order)/.test(searchText) === true) {
    return 'tabler:receipt-2';
  }
  if (/(search|find|lookup)/.test(searchText) === true) {
    return 'tabler:search';
  }
  if (/(database|record|data)/.test(searchText) === true) {
    return 'tabler:database';
  }
  if (/(http|api|webhook|url)/.test(searchText) === true) {
    return 'tabler:cloud-code';
  }
  return 'tabler:tool';
}

function getToolImage(tool, payload) {
  const candidates = [
    payload.integrationImage,
    payload.image,
    payload.logoUrl,
    tool.imageUrl,
    tool.image,
    tool.logoUrl
  ];
  for (let index = 0; index < candidates.length; index = index + 1) {
    if (hasText(candidates[index]) === true) {
      return candidates[index].trim();
    }
  }
  return '';
}

function getNodeDefinitions(tools, selectedChannels = []) {
  const currentChannels = Array.isArray(selectedChannels) === true ? selectedChannels : [];
  const definitions = getBaseDefinitions().filter(definition => {
    if (Array.isArray(definition.supportedChannels) === false) {
      return true;
    }
    return definition.supportedChannels.some(channel => currentChannels.includes(channel) === true);
  });
  const sourceTools = Array.isArray(tools) === true ? tools : [];
  for (let index = 0; index < sourceTools.length; index = index + 1) {
    const tool = sourceTools[index];
    if (tool === null) {
      continue;
    }
    if (tool === undefined) {
      continue;
    }
    if (typeof tool !== 'object') {
      continue;
    }
    const rawId = tool.toolId !== undefined ? tool.toolId : tool.toolid;
    const toolId = Number.parseInt(String(rawId), 10);
    if (Number.isFinite(toolId) === false) {
      continue;
    }
    if (toolId <= 0) {
      continue;
    }
    const rawName = tool.toolName !== undefined ? tool.toolName : tool.toolname;
    const toolName = hasText(rawName) === true ? rawName.trim() : `Tool ${toolId}`;
    const payload = getToolPayload(tool);
    const description = hasText(payload.description) === true ? payload.description.trim() : `Run ${toolName}`;
    const data = getDefaultActionData('activateTool');
    data.label = toolName;
    data.stepDescription = description;
    data.selectedToolId = toolId;
    definitions.push({
      id: `connected-tool-${toolId}`,
      group: 'Your tools',
      type: 'Action',
      label: toolName,
      description,
      icon: getToolIcon(toolName, payload),
      image: getToolImage(tool, payload),
      isConnectedTool: true,
      data
    });
  }
  return definitions;
}

function normalizeNodeData(node) {
  const data = node !== null && node !== undefined && node.data !== null && node.data !== undefined ? deepCopy(node.data) : {};
  const nodeType = hasText(data.nodeType) === true ? data.nodeType : (hasText(node.type) === true ? node.type : 'Question');
  data.nodeType = nodeType;
  if (hasText(data.label) === false) {
    data.label = hasText(node.label) === true ? node.label : 'Untitled step';
  }
  if (hasText(data.stepDescription) === false) {
    data.stepDescription = '';
  }
  if (['Question', 'Root'].includes(nodeType) === true) {
    if (hasText(data.message) === false) {
      data.message = '';
    }
    data.variablesToCollect = Array.isArray(data.variablesToCollect) === true ? data.variablesToCollect : [];
    data.toolsToUse = Array.isArray(data.toolsToUse) === true ? data.toolsToUse : [];
    data.buttons = Array.isArray(data.buttons) === true ? data.buttons : [];
    data.useExactMessage = data.useExactMessage === true;
  }
  if (nodeType === 'KnowledgeBase') {
    data.variablesToCollect = Array.isArray(data.variablesToCollect) === true ? data.variablesToCollect : [];
    data.buttons = Array.isArray(data.buttons) === true ? data.buttons : [];
  }
  if (nodeType === 'If') {
    data.config = data.config !== null && data.config !== undefined && typeof data.config === 'object' ? data.config : {};
    data.config.sourceType = hasText(data.config.sourceType) === true ? data.config.sourceType : 'variable';
    data.config.sourceKey = hasText(data.config.sourceKey) === true ? data.config.sourceKey : '';
    data.config.operator = hasText(data.config.operator) === true ? data.config.operator : 'equals';
    data.config.left = hasText(data.config.left) === true ? data.config.left : '';
    data.config.right = hasText(data.config.right) === true ? data.config.right : '';
  }
  if (nodeType === 'Action') {
    const actionType = hasText(data.actionType) === true ? data.actionType : 'activateTool';
    const defaults = getDefaultActionData(actionType);
    const originalConfig = data.config !== null && data.config !== undefined && typeof data.config === 'object' ? data.config : {};
    const originalInlineTool = data.inlineTool !== null && data.inlineTool !== undefined && typeof data.inlineTool === 'object' ? data.inlineTool : {};
    const merged = { ...defaults, ...data };
    merged.config = { ...defaults.config, ...originalConfig };
    merged.inlineTool = { ...defaults.inlineTool, ...originalInlineTool };
    merged.variablesToCollect = Array.isArray(data.variablesToCollect) === true ? data.variablesToCollect : [];
    merged.buttons = Array.isArray(data.buttons) === true ? data.buttons : [];
    merged.toolInputMappings = data.toolInputMappings !== null && data.toolInputMappings !== undefined && typeof data.toolInputMappings === 'object' ? data.toolInputMappings : {};
    return merged;
  }
  return data;
}

function normalizeFlow(flow, startingMessage) {
  const source = Array.isArray(flow) === true ? flow : [];
  const nodes = [];
  const edges = [];
  let rootNode = null;
  for (let index = 0; index < source.length; index = index + 1) {
    const element = source[index];
    if (element === null) {
      continue;
    }
    if (element === undefined) {
      continue;
    }
    if (element.source !== undefined && element.target !== undefined) {
      edges.push({
        id: hasText(element.id) === true ? element.id : `edge-${element.source}-${element.target}-${index}`,
        source: element.source,
        target: element.target,
        type: 'smoothstep',
        label: hasText(element.label) === true ? element.label : '',
        data: {
          ...(element.data !== null && element.data !== undefined ? element.data : {}),
          ifCondition: element.data !== null && element.data !== undefined && hasText(element.data.ifCondition) === true ? element.data.ifCondition : (hasText(element.label) === true ? element.label : '')
        }
      });
      continue;
    }
    if (element.position === null) {
      continue;
    }
    if (element.position === undefined) {
      continue;
    }
    const isRoot = [
      element.id === 'root-0',
      element.type === 'Root',
      element.type === 'root',
      element.data !== null && element.data !== undefined && element.data.nodeType === 'Root',
      element.data !== null && element.data !== undefined && element.data.isRoot === true
    ].includes(true);
    if (isRoot === true) {
      if (rootNode === null) {
        rootNode = element;
      }
      continue;
    }
    const data = normalizeNodeData(element);
    nodes.push({
      ...element,
      type: data.nodeType,
      label: data.label,
      data,
      draggable: true
    });
  }
  const root = makeRootNode(startingMessage, rootNode);
  const endCallIds = new Set();
  for (let index = 0; index < nodes.length; index = index + 1) {
    if (nodes[index].data.actionType === 'endCall') {
      endCallIds.add(nodes[index].id);
    }
  }
  const safeEdges = edges.filter(edge => endCallIds.has(edge.source) === false);
  return [root, ...nodes, ...safeEdges];
}

function serializeFlow(elements, startingMessage) {
  const normalized = normalizeFlow(elements, startingMessage);
  const serialized = [];
  for (let index = 0; index < normalized.length; index = index + 1) {
    const element = normalized[index];
    if (element.source !== undefined && element.target !== undefined) {
      serialized.push({
        id: element.id,
        source: element.source,
        target: element.target,
        type: 'smoothstep',
        label: hasText(element.label) === true ? element.label : '',
        data: {
          ...(element.data !== null && element.data !== undefined ? element.data : {}),
          ifCondition: element.data !== null && element.data !== undefined && hasText(element.data.ifCondition) === true ? element.data.ifCondition : (hasText(element.label) === true ? element.label : '')
        }
      });
      continue;
    }
    const serializedData = deepCopy(element.data);
    delete serializedData.flowManagerIssueCount;
    serialized.push({
      id: element.id,
      type: element.type,
      position: element.position,
      label: element.data.label,
      data: serializedData,
      draggable: element.draggable !== false
    });
  }
  return serialized;
}

function autoLayout(elements) {
  const nodes = elements.filter(element => element.position !== undefined && element.position !== null);
  const edges = elements.filter(element => element.source !== undefined && element.target !== undefined);
  const root = nodes.find(node => node.data !== null && node.data !== undefined && node.data.nodeType === 'Root');
  if (root === undefined) {
    return elements;
  }
  const nodeIds = new Set();
  const adjacency = new Map();
  for (let index = 0; index < nodes.length; index = index + 1) {
    nodeIds.add(nodes[index].id);
    adjacency.set(nodes[index].id, []);
  }
  for (let index = 0; index < edges.length; index = index + 1) {
    const edge = edges[index];
    if (adjacency.has(edge.source) === true && nodeIds.has(edge.target) === true) {
      adjacency.get(edge.source).push(edge.target);
    }
  }
  const levels = new Map();
  const queue = [root.id];
  levels.set(root.id, 0);
  let queueIndex = 0;
  while (queueIndex < queue.length) {
    const currentId = queue[queueIndex];
    queueIndex = queueIndex + 1;
    const children = adjacency.get(currentId);
    const currentLevel = levels.get(currentId);
    for (let childIndex = 0; childIndex < children.length; childIndex = childIndex + 1) {
      const childId = children[childIndex];
      const nextLevel = currentLevel + 1;
      if (levels.has(childId) === false) {
        levels.set(childId, nextLevel);
        queue.push(childId);
      }
    }
  }
  let disconnectedLevel = 1;
  for (let index = 0; index < nodes.length; index = index + 1) {
    if (levels.has(nodes[index].id) === false) {
      levels.set(nodes[index].id, disconnectedLevel);
      disconnectedLevel = disconnectedLevel + 1;
    }
  }
  const grouped = new Map();
  for (let index = 0; index < nodes.length; index = index + 1) {
    const level = levels.get(nodes[index].id);
    if (grouped.has(level) === false) {
      grouped.set(level, []);
    }
    grouped.get(level).push(nodes[index].id);
  }
  const positions = new Map();
  const sortedLevels = Array.from(grouped.keys()).sort((first, second) => first - second);
  for (let index = 0; index < sortedLevels.length; index = index + 1) {
    const level = sortedLevels[index];
    const ids = grouped.get(level);
    const totalHeight = ids.length * 164;
    let y = 250 - totalHeight / 2;
    for (let nodeIndex = 0; nodeIndex < ids.length; nodeIndex = nodeIndex + 1) {
      positions.set(ids[nodeIndex], { x: 80 + level * 330, y });
      y = y + 164;
    }
  }
  return elements.map(element => {
    if (positions.has(element.id) === true) {
      return { ...element, position: positions.get(element.id) };
    }
    return element;
  });
}

function validateFlow(elements) {
  const nodes = elements.filter(element => element.position !== undefined && element.position !== null);
  const edges = elements.filter(element => element.source !== undefined && element.target !== undefined);
  const issues = [];
  const connectedTargets = new Set();
  const outgoing = new Map();
  for (let index = 0; index < edges.length; index = index + 1) {
    connectedTargets.add(edges[index].target);
    outgoing.set(edges[index].source, true);
  }
  for (let index = 0; index < nodes.length; index = index + 1) {
    const node = nodes[index];
    const isRoot = node.data.nodeType === 'Root';
    if (isRoot === false && connectedTargets.has(node.id) === false) {
      issues.push({ id: `${node.id}-unreachable`, nodeId: node.id, text: `${node.data.label} is not connected from a previous step.` });
    }
    if (node.data.actionType !== 'endCall' && outgoing.has(node.id) === false) {
      issues.push({ id: `${node.id}-ending`, nodeId: node.id, text: `${node.data.label} has no next step.` });
    }
    if (node.data.nodeType === 'Question' && hasText(node.data.message) === false && hasText(node.data.stepDescription) === false) {
      issues.push({ id: `${node.id}-empty`, nodeId: node.id, text: `${node.data.label} needs a message or a clear description.` });
    }
    if (node.data.nodeType === 'If' && hasText(node.data.config.sourceKey) === false && hasText(node.data.config.left) === false) {
      issues.push({ id: `${node.id}-condition`, nodeId: node.id, text: `${node.data.label} needs information to check.` });
    }
    if (node.data.actionType === 'activateTool' && node.data.selectedToolId === null && node.data.toolSource !== 'inlineJsTool') {
      issues.push({ id: `${node.id}-tool`, nodeId: node.id, text: `${node.data.label} needs a tool.` });
    }
    if (node.data.config !== null && node.data.config !== undefined && node.data.config.isJavaScriptNode === true && hasText(node.data.inlineTool.functionConfig) === false) {
      issues.push({ id: `${node.id}-javascript`, nodeId: node.id, text: `${node.data.label} needs JavaScript to run.` });
    }
    if (node.data.actionType === 'httpRequest' && hasText(node.data.config.url) === false) {
      issues.push({ id: `${node.id}-url`, nodeId: node.id, text: `${node.data.label} needs an API address.` });
    }
    if (node.data.actionType === 'transferCall' && hasText(node.data.config.destination) === false) {
      issues.push({ id: `${node.id}-destination`, nodeId: node.id, text: `${node.data.label} needs a transfer destination.` });
    }
  }
  return issues;
}

function getNodeAppearance(data) {
  const configuredIcon = hasText(data.flowManagerIcon) === true ? data.flowManagerIcon : '';
  if (data.nodeType === 'Root') {
    return { label: 'Start', icon: configuredIcon !== '' ? configuredIcon : 'tabler:player-play', tone: 'start' };
  }
  if (data.nodeType === 'Question') {
    return { label: 'Conversation', icon: configuredIcon !== '' ? configuredIcon : 'tabler:message-circle', tone: 'conversation' };
  }
  if (data.nodeType === 'KnowledgeBase') {
    return { label: 'Knowledge', icon: configuredIcon !== '' ? configuredIcon : 'tabler:database-search', tone: 'knowledge' };
  }
  if (data.nodeType === 'If') {
    return { label: 'Decision', icon: configuredIcon !== '' ? configuredIcon : 'tabler:git-branch', tone: 'decision' };
  }
  if (data.actionType === 'httpRequest') {
    return { label: 'API request', icon: configuredIcon !== '' ? configuredIcon : 'tabler:world-www', tone: 'action' };
  }
  if (data.config !== null && data.config !== undefined && data.config.isJavaScriptNode === true) {
    return { label: 'JavaScript', icon: configuredIcon !== '' ? configuredIcon : 'tabler:code', tone: 'action' };
  }
  if (data.actionType === 'announce') {
    return { label: 'Announcement', icon: configuredIcon !== '' ? configuredIcon : 'tabler:speakerphone', tone: 'phone' };
  }
  if (data.actionType === 'transferCall') {
    return { label: 'Call transfer', icon: configuredIcon !== '' ? configuredIcon : 'tabler:phone-outgoing', tone: 'phone' };
  }
  if (data.actionType === 'endCall') {
    return { label: 'End call', icon: configuredIcon !== '' ? configuredIcon : 'tabler:phone-off', tone: 'danger' };
  }
  return { label: 'Action', icon: configuredIcon !== '' ? configuredIcon : 'tabler:tool', tone: 'action' };
}

function getNodeSummary(data) {
  const candidates = [data.stepDescription, data.message];
  const isConnectedTool = data.flowManagerIsConnectedTool === true || (data.actionType === 'activateTool' && data.selectedToolId !== null && data.selectedToolId !== undefined);
  const maximumLength = isConnectedTool === true ? 72 : 110;
  if (data.actionType === 'httpRequest' && data.config !== null && data.config !== undefined && hasText(data.config.url) === true) {
    candidates.unshift(`${hasText(data.config.method) === true ? data.config.method : 'POST'} ${data.config.url}`);
  }
  if (data.actionType === 'transferCall' && data.config !== null && data.config !== undefined && hasText(data.config.destination) === true) {
    candidates.unshift(data.config.destination);
  }
  if (data.nodeType === 'If' && data.config !== null && data.config !== undefined) {
    const source = hasText(data.config.sourceKey) === true ? data.config.sourceKey : data.config.left;
    if (hasText(source) === true) {
      candidates.unshift(`${source} ${hasText(data.config.operator) === true ? data.config.operator : 'equals'} ${hasText(data.config.right) === true ? data.config.right : ''}`.trim());
    }
  }
  for (let index = 0; index < candidates.length; index = index + 1) {
    if (hasText(candidates[index]) === true) {
      const value = candidates[index].trim();
      return value.length > maximumLength ? `${value.slice(0, maximumLength - 3)}...` : value;
    }
  }
  return 'Select this step to finish setting it up.';
}

export {
  autoLayout,
  deepCopy,
  getNodeAppearance,
  getNodeDefinitions,
  getNodeSummary,
  getStartingMessageText,
  hasText,
  makeRootNode,
  normalizeFlow,
  serializeFlow,
  validateFlow
};
